import { uniqBy } from 'lodash-es';
import { v4 as uuidV4 } from 'uuid';
import {
  BaseAccommodationLegSchemaValues,
  BaseAviaLegSchemaValues,
  BaseCommercialAviaLegSchemaValues,
  BaseCurriServiceGroundSchemaValues,
  BaseSelfServicedGroundSchemaValues,
  BaseSupplementalLiftAviaSchemaValues,
  BaseTmdxAffiliateGroundSchemaValues,
  BaseTmdxAviaLegSchemaValues,
  BaseTmdxDriveAffiliateGroundSchemaValues,
  BaseTmdxDriveTedsGroundSchemaValues,
  BaseTxCenterGroundSchemaValues,
  BaseTxProviderAviaSchemaValues,
  CommonLegResourcesSchemaValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  DEFAULT_ACCOMMODATION_VALUES,
  DEFAULT_COMMERCIAL_AVIA_VALUES,
  DEFAULT_DEVICE_DELIVERY_VALUES,
  DEFAULT_SELF_SERVICED_VALUES,
  DEFAULT_SUPPLEMENTAL_LIFT_VALUES,
  DEFAULT_TMDX_AFFILIATE_VALUES,
  DEFAULT_TMDX_AVIA_VALUES,
  DEFAULT_TX_CENTER_AVIA_VALUES,
  DEFAULT_TX_CENTER_GROUND_VALUES,
  DEFAULT_TMDX_DRIVE_AFFILIATE_VALUES,
  DEFAULT_TMDX_DRIVE_TEDS_VALUES,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation/consts';
import {
  getAirportLocationValues,
  getDateTimeInLocationTz,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils/locations';
import {
  getDefaultAviaResource,
  getValueBySegment,
  getValuesByPrevLeg,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils/baseValues';
import {
  getCurrentAccommodationLegFormValues,
  getCurrentCommercialFlightAviaLegFormValues,
  getCurrentCurriServiceGroundLegFormValues,
  getCurrentSupplementalLiftAviaLegFormValues,
  getCurrentTmdxAffiliateGroundLegFormValues,
  getCurrentTmdxAviaLegFormValues,
  getCurrentTxCenterProviderAviaLegFormValues,
  getCurrentTxCenterProviderGroundLegFormValues,
  getCurrentTmdxDriveAffiliateGroundLegFormValues,
  getCurrentTmdxDriveTedsGroundLegFormValues,
  getCurrentSelfServicedGroundLegFormValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils/currentValues';
import {
  AviaLegTypes,
  GroundLegTypes,
  LegTypeEnum,
  RouteLegType,
  RouteLegTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { RouteResourcesType, RouteSegmentType } from '@/types/route';
import { AVIA_LEG_TYPES, GROUND_LEG_TYPES, REPOSITION_SEGMENT_TYPES } from '@/utils/route';

type GetDefaultLegFormValuesProps = {
  logisticsId: string;
  currentLegType: RouteLegTypeEnum;
  segment: RouteSegmentType | null;
  prevLeg: RouteLegType | null;
  isNewSegment: boolean;
  resources?: RouteResourcesType;
  legs: (AviaLegTypes | GroundLegTypes)[];
};

export const getDefaultTmdxAviaFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
  legs,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources'>): BaseTmdxAviaLegSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.TmdxJetAviaLeg);
  const defaultAviaResource = getDefaultAviaResource(legs);

  return {
    ...(DEFAULT_TMDX_AVIA_VALUES as BaseTmdxAviaLegSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseTmdxAviaLegSchemaValues>),
    ...(prevLegValues as Partial<BaseTmdxAviaLegSchemaValues>),
    ...defaultAviaResource,
  };
};

export const getDefaultSupplementalLiftFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
  legs,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources'>): BaseSupplementalLiftAviaSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.SupplementalLiftAviaLeg);
  const supplementalLiftAviaLeg = legs.find((l) => l.legType === RouteLegTypeEnum.SupplementalLiftAviaLeg);
  const providerId = supplementalLiftAviaLeg?.provider.providerId;
  const priceInCents = supplementalLiftAviaLeg?.priceInCents;

  return {
    ...(DEFAULT_SUPPLEMENTAL_LIFT_VALUES as BaseSupplementalLiftAviaSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseSupplementalLiftAviaSchemaValues>),
    ...(prevLegValues as Partial<BaseSupplementalLiftAviaSchemaValues>),
    supplementalLiftProviderId: providerId || '',
    priceInCents: priceInCents ? `${(priceInCents / 100).toFixed(2)}` : '',
  };
};

export const getDefaultTxProviderAviaFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
  legs,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources'>): BaseTxProviderAviaSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.TxCenterProviderAviaLeg);
  const providerId = legs.find(
    (l) =>
      l.legType === RouteLegTypeEnum.TxCenterProviderAviaLeg ||
      l.legType === RouteLegTypeEnum.TxCenterProviderGroundLeg,
  )?.provider.providerId;

  return {
    ...(DEFAULT_TX_CENTER_AVIA_VALUES as BaseTxProviderAviaSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseTxProviderAviaSchemaValues>),
    ...(prevLegValues as Partial<BaseTxProviderAviaSchemaValues>),
    txCenterProviderId: providerId || '',
  };
};

export const getDefaultCommercialAviaFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources' | 'legs'>): BaseCommercialAviaLegSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.CommercialFlightAviaLeg);

  return {
    ...(DEFAULT_COMMERCIAL_AVIA_VALUES as BaseCommercialAviaLegSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseCommercialAviaLegSchemaValues>),
    ...(prevLegValues as Partial<BaseCommercialAviaLegSchemaValues>),
  };
};

export const getDefaultTmdxAffiliateFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
}: Omit<
  GetDefaultLegFormValuesProps,
  'currentLegType' | 'resources' | 'legs'
>): BaseTmdxAffiliateGroundSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.TmdxAffiliateGroundLeg);

  return {
    ...(DEFAULT_TMDX_AFFILIATE_VALUES as BaseTmdxAffiliateGroundSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseTmdxAffiliateGroundSchemaValues>),
    ...(prevLegValues as Partial<BaseTmdxAffiliateGroundSchemaValues>),
  };
};

export const getDefaultTmdxDriveAffiliateFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
}: Omit<
  GetDefaultLegFormValuesProps,
  'currentLegType' | 'resources' | 'legs'
>): BaseTmdxDriveAffiliateGroundSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg);

  return {
    ...(DEFAULT_TMDX_DRIVE_AFFILIATE_VALUES as BaseTmdxDriveAffiliateGroundSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseTmdxDriveAffiliateGroundSchemaValues>),
    ...(prevLegValues as Partial<BaseTmdxDriveAffiliateGroundSchemaValues>),
  };
};

export const getDefaultTxCenterGroundFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
  legs,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources'>): BaseTxCenterGroundSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.TxCenterProviderGroundLeg);
  const providerId = legs.find(
    (l) =>
      l.legType === RouteLegTypeEnum.TxCenterProviderAviaLeg ||
      l.legType === RouteLegTypeEnum.TxCenterProviderGroundLeg,
  )?.provider.providerId;

  return {
    ...(DEFAULT_TX_CENTER_GROUND_VALUES as BaseTxCenterGroundSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseTxCenterGroundSchemaValues>),
    ...(prevLegValues as Partial<BaseTxCenterGroundSchemaValues>),
    txCenterProviderId: providerId || '',
  };
};

export const getDefaultCurriServiceGroundFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources' | 'legs'>): BaseCurriServiceGroundSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.CurriServiceGroundLeg);

  return {
    ...(DEFAULT_DEVICE_DELIVERY_VALUES as BaseCurriServiceGroundSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseCurriServiceGroundSchemaValues>),
    ...(prevLegValues as Partial<BaseCurriServiceGroundSchemaValues>),
  };
};

export const getDefaultTmdxDriveTedsFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
}: Omit<
  GetDefaultLegFormValuesProps,
  'currentLegType' | 'resources' | 'legs'
>): BaseTmdxDriveTedsGroundSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.TmdxDriveTedsGroundLeg);

  return {
    ...(DEFAULT_TMDX_DRIVE_TEDS_VALUES as BaseTmdxDriveTedsGroundSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseTmdxDriveTedsGroundSchemaValues>),
    ...(prevLegValues as Partial<BaseTmdxDriveTedsGroundSchemaValues>),
  };
};

export const getDefaultSelfServicedGroundFormValues = ({
  logisticsId,
  segment,
  prevLeg,
  isNewSegment,
}: Omit<GetDefaultLegFormValuesProps, 'currentLegType' | 'resources' | 'legs'>): BaseSelfServicedGroundSchemaValues => {
  const segmentValues = getValueBySegment(segment, isNewSegment);
  const prevLegValues = getValuesByPrevLeg(logisticsId, prevLeg, RouteLegTypeEnum.SelfServicedGroundLeg);

  return {
    ...(DEFAULT_SELF_SERVICED_VALUES as BaseSelfServicedGroundSchemaValues),
    id: uuidV4(),
    ...(segmentValues as Partial<BaseSelfServicedGroundSchemaValues>),
    ...(prevLegValues as Partial<BaseSelfServicedGroundSchemaValues>),
  };
};

export const getDefaultAccommodationFormValues = (): BaseAccommodationLegSchemaValues => ({
  ...DEFAULT_ACCOMMODATION_VALUES,
  id: uuidV4(),
});

export type GetDefaultLegFormValuesReturnType =
  | BaseTmdxAviaLegSchemaValues
  | BaseSupplementalLiftAviaSchemaValues
  | BaseTxProviderAviaSchemaValues
  | BaseCommercialAviaLegSchemaValues
  | BaseTmdxAffiliateGroundSchemaValues
  | BaseTmdxDriveAffiliateGroundSchemaValues
  | BaseTxCenterGroundSchemaValues
  | BaseCurriServiceGroundSchemaValues
  | BaseTmdxDriveTedsGroundSchemaValues
  | BaseSelfServicedGroundSchemaValues
  | BaseAccommodationLegSchemaValues;

export const getDefaultLegFormValues = ({
  currentLegType,
  logisticsId,
  prevLeg,
  isNewSegment,
  segment,
  legs,
}: GetDefaultLegFormValuesProps): GetDefaultLegFormValuesReturnType => {
  const isCurrentReposition = !!segment?.type && REPOSITION_SEGMENT_TYPES.includes(segment.type);
  const isNewSegmentForGroundLegs = isNewSegment || isCurrentReposition;
  switch (currentLegType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      return getDefaultTmdxAviaFormValues({ logisticsId, prevLeg, isNewSegment, segment, legs });
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
      return getDefaultSupplementalLiftFormValues({ logisticsId, prevLeg, isNewSegment, segment, legs });
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return getDefaultTxProviderAviaFormValues({ logisticsId, prevLeg, isNewSegment, segment, legs });
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return getDefaultCommercialAviaFormValues({ logisticsId, prevLeg, isNewSegment, segment });
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
      return getDefaultTxCenterGroundFormValues({
        logisticsId,
        prevLeg,
        isNewSegment: isNewSegmentForGroundLegs,
        segment,
        legs,
      });
    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return getDefaultCurriServiceGroundFormValues({
        logisticsId,
        prevLeg,
        isNewSegment: isNewSegmentForGroundLegs,
        segment,
      });
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return getDefaultTmdxDriveTedsFormValues({
        logisticsId,
        prevLeg,
        isNewSegment: isNewSegmentForGroundLegs,
        segment,
      });
    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return getDefaultSelfServicedGroundFormValues({
        logisticsId,
        prevLeg,
        isNewSegment: isNewSegmentForGroundLegs,
        segment,
      });
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return getDefaultTmdxAffiliateFormValues({
        logisticsId,
        prevLeg,
        isNewSegment: isNewSegmentForGroundLegs,
        segment,
      });
    case RouteLegTypeEnum.AccommodationLeg:
      return getDefaultAccommodationFormValues();
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
    default:
      return getDefaultTmdxDriveAffiliateFormValues({
        logisticsId,
        prevLeg,
        isNewSegment: isNewSegmentForGroundLegs,
        segment,
      });
  }
};

const getDefaultGroundValues = (
  currentValues: CommonLegResourcesSchemaValues,
  initialSegment: RouteSegmentType | null,
  durationData?: Record<string, number>,
): Partial<CommonLegResourcesSchemaValues> => {
  const segmentType = REPOSITION_SEGMENT_TYPES.includes(currentValues.segmentType)
    ? initialSegment?.type
    : currentValues.segmentType;
  const groupId = REPOSITION_SEGMENT_TYPES.includes(currentValues.segmentType)
    ? initialSegment?.id
    : currentValues.groupId;
  const arrivalTimeStringInLocationTz = getDateTimeInLocationTz(currentValues, durationData);

  return {
    id: currentValues.id,
    departureDateTime: currentValues.departureDateTime,
    departureDateTimeValue: currentValues.departureDateTimeValue,
    departureLocalTimezone: currentValues.departureLocalTimezone,
    minDate: currentValues.minDate,
    maxDate: currentValues.maxDate,
    departureLocation: currentValues.departureLocation,
    arrivalLocation: currentValues.arrivalLocation,
    arrivalDateTimeValue: durationData?.timeDurationInMs
      ? arrivalTimeStringInLocationTz
      : currentValues.arrivalDateTimeValue,
    arrivalDateTime: durationData?.timeDurationInMs
      ? arrivalTimeStringInLocationTz.toString()
      : currentValues.arrivalDateTime,
    arrivalLocalTimezone: currentValues.arrivalLocalTimezone,
    isDateTimeLinked: currentValues.isDateTimeLinked,
    resources: currentValues.resources.filter(
      (r) => ![CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType),
    ),
    comments: currentValues.comments,
    ...(durationData || {}),
    ...(segmentType ? { segmentType: segmentType as BaseTmdxAffiliateGroundSchemaValues['segmentType'] } : {}),
    ...(groupId ? { groupId } : {}),
  };
};

const getDefaultAviaValues = (currentValues: CommonLegResourcesSchemaValues): Partial<BaseAviaLegSchemaValues> => {
  return {
    id: currentValues.id,
    minDate: currentValues.minDate,
    maxDate: currentValues.maxDate,
    isDateTimeLinked: currentValues.isDateTimeLinked,
    resources: currentValues.resources,
    comments: currentValues.comments,
    segmentType: currentValues.segmentType as CommonLegResourcesSchemaValues['segmentType'],
    groupId: currentValues.groupId,
  };
};

const getAviaLocationValues = (
  currentLegType: RouteLegTypeEnum,
  currentValues: CommonLegResourcesSchemaValues,
  defaultValues: GetDefaultLegFormValuesReturnType,
  durationData?: Record<string, number>,
) => {
  return currentLegType === RouteLegTypeEnum.CommercialFlightAviaLeg
    ? {}
    : getAirportLocationValues(currentValues, defaultValues as BaseAviaLegSchemaValues, durationData);
};

const getTmdxAviaResources = (isReposition: boolean, allResources: CommonLegResourcesSchemaValues['resources']) => {
  if (isReposition) {
    return allResources.filter((r) => [CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType));
  }
  return allResources;
};

type GetDefaultValuesByLegTypeParams = {
  legType: RouteLegTypeEnum;
  currentValues: CommonLegResourcesSchemaValues;
  defaultValues: GetDefaultLegFormValuesReturnType;
  initialSegment?: RouteSegmentType | null;
  durationData?: Record<string, number>;
};

export const getDefaultValuesByLegType = ({
  legType,
  currentValues,
  defaultValues,
  initialSegment = null,
  durationData,
}: GetDefaultValuesByLegTypeParams): CommonLegResourcesSchemaValues | BaseAccommodationLegSchemaValues => {
  const isReposition = REPOSITION_SEGMENT_TYPES.includes(currentValues.segmentType);
  const { legType: currentLegType, formType, ...currentFormValues } = currentValues;

  const mainResources = currentValues.resources.filter(
    (r) => ![CaseResourceType.Crew, CaseResourceType.Aircraft].includes(r.resourceType),
  );

  const isPrevAccommodationLeg = currentLegType === RouteLegTypeEnum.AccommodationLeg;

  if (isPrevAccommodationLeg) {
    return {
      ...defaultValues,
      resources: mainResources,
    };
  }

  switch (legType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      const resources = uniqBy([...defaultValues.resources, ...currentValues.resources], 'resourceId');

      return {
        ...(defaultValues as BaseTmdxAviaLegSchemaValues),
        ...getDefaultAviaValues(currentValues),
        ...getAviaLocationValues(currentLegType, currentValues, defaultValues, durationData),
        resources: getTmdxAviaResources(isReposition, resources),
      };

    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
      return {
        ...(defaultValues as BaseSupplementalLiftAviaSchemaValues),
        ...getDefaultAviaValues(currentValues),
        ...getAviaLocationValues(currentLegType, currentValues, defaultValues, durationData),
        resources: isReposition ? [] : mainResources,
      };

    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return {
        ...(defaultValues as BaseTxProviderAviaSchemaValues),
        ...getDefaultAviaValues(currentValues),
        ...getAviaLocationValues(currentLegType, currentValues, defaultValues, durationData),
        resources: isReposition ? [] : mainResources,
      };

    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return {
        ...(defaultValues as BaseCommercialAviaLegSchemaValues),
        ...getDefaultAviaValues(currentValues),
        resources: mainResources,
      };

    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return {
        ...(defaultValues as BaseTmdxAffiliateGroundSchemaValues),
        ...(currentLegType !== RouteLegTypeEnum.TxCenterProviderGroundLeg
          ? getDefaultGroundValues(currentValues, initialSegment, durationData)
          : currentFormValues),
      };

    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
      return {
        ...(defaultValues as BaseTmdxDriveAffiliateGroundSchemaValues),
        ...getDefaultGroundValues(currentValues, initialSegment, durationData),
      };

    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
      return {
        ...(defaultValues as BaseTxCenterGroundSchemaValues),
        ...(currentLegType !== RouteLegTypeEnum.TmdxAffiliateGroundLeg
          ? getDefaultGroundValues(currentValues, initialSegment, durationData)
          : currentFormValues),
      };

    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return {
        ...(defaultValues as BaseCurriServiceGroundSchemaValues),
        ...getDefaultGroundValues(currentValues, null),
        isDateTimeLinked: false,
      };

    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return {
        ...(defaultValues as BaseTmdxDriveTedsGroundSchemaValues),
        ...getDefaultGroundValues(currentValues, initialSegment, durationData),
      };

    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return {
        ...(defaultValues as BaseSelfServicedGroundSchemaValues),
        ...getDefaultGroundValues(currentValues, initialSegment, durationData),
      };

    default:
      return {
        ...(defaultValues as BaseAccommodationLegSchemaValues),
        resources: mainResources,
      };
  }
};

type GetDefaultLegTypeParams = {
  legs: (AviaLegTypes | GroundLegTypes)[];
  isTmdxDriveCompatible: boolean;
  formType?: LegTypeEnum;
  isDisabledRegion?: boolean;
};

export const getDefaultLegType = ({
  legs,
  isTmdxDriveCompatible,
  formType = LegTypeEnum.ground,
  isDisabledRegion = false,
}: GetDefaultLegTypeParams): RouteLegTypeEnum => {
  if (formType === LegTypeEnum.accommodation) {
    return RouteLegTypeEnum.AccommodationLeg;
  }

  if (formType === LegTypeEnum.ground) {
    const baseTmdxGroundLegType = isTmdxDriveCompatible
      ? RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg
      : RouteLegTypeEnum.TmdxAffiliateGroundLeg;
    const isGroundTXCenterSelected = legs.some((l) => l.legType === RouteLegTypeEnum.TxCenterProviderGroundLeg);

    return isGroundTXCenterSelected ? RouteLegTypeEnum.TxCenterProviderGroundLeg : baseTmdxGroundLegType;
  }

  const hasAirPartner = legs.some((l) => l.legType === RouteLegTypeEnum.SupplementalLiftAviaLeg);
  const hasAirTXCenter = legs.some((l) => l.legType === RouteLegTypeEnum.TxCenterProviderAviaLeg);
  const hasGroundTXCenter = legs.some((l) => l.legType === RouteLegTypeEnum.TxCenterProviderGroundLeg);

  if (hasGroundTXCenter || hasAirTXCenter) return RouteLegTypeEnum.TxCenterProviderAviaLeg;
  if (hasAirPartner || isDisabledRegion) return RouteLegTypeEnum.SupplementalLiftAviaLeg;

  return RouteLegTypeEnum.TmdxJetAviaLeg;
};

export type GetCurrentLegFormValues<T extends RouteLegType> = {
  currentLeg: T;
  logisticsId: string;
  segment: RouteSegmentType | null;
};

export const getCurrentLegFormValues = <T extends RouteLegType>({
  currentLeg,
  logisticsId,
  segment,
}: GetCurrentLegFormValues<T>): GetDefaultLegFormValuesReturnType => {
  switch (currentLeg.legType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      return getCurrentTmdxAviaLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
      return getCurrentSupplementalLiftAviaLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return getCurrentTxCenterProviderAviaLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return getCurrentCommercialFlightAviaLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
      return getCurrentTxCenterProviderGroundLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return getCurrentCurriServiceGroundLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.AccommodationLeg:
      return getCurrentAccommodationLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return getCurrentTmdxDriveTedsGroundLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return getCurrentSelfServicedGroundLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return getCurrentTmdxAffiliateGroundLegFormValues({ currentLeg, logisticsId, segment });
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
    default:
      return getCurrentTmdxDriveAffiliateGroundLegFormValues({ currentLeg, logisticsId, segment });
  }
};

type UpdateRepositionCurrentValuesParams = {
  currentValues: CommonLegResourcesSchemaValues;
  initialSegment: RouteSegmentType | null;
  nextLegType: RouteLegTypeEnum;
  segmentFieldState: { isTouched: boolean };
};

export const updateRepositionCurrentValues = ({
  currentValues,
  initialSegment,
  segmentFieldState,
  nextLegType,
}: UpdateRepositionCurrentValuesParams) => {
  const isInitialReposition = initialSegment?.type && REPOSITION_SEGMENT_TYPES.includes(initialSegment.type);

  if (!isInitialReposition) return currentValues;

  const isNextGroundLeg = GROUND_LEG_TYPES.includes(nextLegType);
  const initialGroundSegmentType = isInitialReposition
    ? SegmentTypeEnum.custom
    : initialSegment?.type || SegmentTypeEnum.custom;

  const isNextAviaLeg = AVIA_LEG_TYPES.includes(nextLegType);
  const isSegmentFieldTouched = segmentFieldState.isTouched;
  const initialAviaSegmentType = isSegmentFieldTouched
    ? currentValues.segmentType
    : initialSegment?.type || SegmentTypeEnum.custom;

  const isNextNewSegment = isNextGroundLeg || isNextAviaLeg;
  const nextInitialSegmentType = isNextAviaLeg ? initialAviaSegmentType : initialGroundSegmentType;
  const isTheSameSegmentType = initialSegment?.type === nextInitialSegmentType;

  return {
    ...currentValues,
    segmentType: isNextNewSegment ? nextInitialSegmentType : currentValues.segmentType,
    groupId: isTheSameSegmentType ? initialSegment.id : currentValues.groupId,
  };
};
